import React from 'react';
import { Field, CccisdInput, CccisdToggle } from 'cccisd-formik';
import { BuilderBlock, reportTemplateBuilder } from 'cccisd-laravel-appdefs';
import { startCase } from 'lodash';

export const initialValues = {
    cover: {
        show: true,
        title: 'Building Healing Systems',
        subtitle: 'Trauma-Informed Organizational Assessment',
        detailsLine1: 'Assessment items adapted with permission from the National Center for Child Traumatic Stress.',
        detailsLine2:
            'Halladay Goldman, J., Purbeck Trunzo, C., and Agosti, J. (2019). NCTSN Trauma-Informed Organizational',
        detailsLine3: 'Assessment. Los Angeles, CA, and Durham, NC: National Center for Child Traumatic Stress,',
        pageNumber: '0',
    },
    page1: {
        show: true,
        title: 'Understanding this Summary',
        paragraph1:
            'This report is generated from information your organization provided. The composite score is the average of the rating for every item within the domain.',
        paragraph2:
            'Domains where contributors reported the practices within the domain do not exist yet within the organization and the overall mean falls between 1.0–1.9. Scores in this range indicate that an organization is functioning in a way that is trauma unaware or, at best, where some practices may align with a "trauma-aware" approach (i.e., the organization and workforce may have a basic awareness of trauma and its impacts).',
        paragraph3: `Domains where contributors reported the practices within the domain exist and they are carried out periodically but are not standard within the organization. The overall mean falls between 2.0–3.9. Scores in this category indicate that the organization's practices range from a "trauma-sensitive" approach to a "trauma-responsive" approach.`,
        paragraph4:
            'Domains where contributors reported the practices within the domain are part of the everyday practice, and seen in training and protocols. The overall mean falls between 4.0–5.0. Scores in this range indicate the organization is functioning in ways most aligned with a "trauma-informed" approach, where the organization has fully integrated all core trauma-informed principles throughout all levels of its infrastructure. The organizational practices are also healing centered, such that they are culturally responsive and foster equitable culture and care.',
        pageNumber: '1',
    },
    page2: {
        show: true,
        title: 'Understanding this Summary',
        subheader: 'Building Healing Systems Domains',
        pageNumber: '2',
    },
    page3: {
        show: true,
        title: 'Trauma-Informed Screening, Assessment, Care-Planning, and Treatment',
        pageNumber: '3',
    },
    page4: {
        show: true,

        question1:
            'Staff who conduct screenings or assessments demonstrate trauma-informed skills, including building safety and trust, recognizing trauma reminders and warning signs, and supporting emotional regulation.',
        question2:
            'Clinical screening processes are sensitive to differences in identity factors, e.g., age, culture, disability status, ethnicity, gender identity, language, race, and sexual orientation.',
        question3:
            'Prior to screenings/assessments, clients are informed of the purpose, their to right consent or refuse, how results will be used, and who will have access to the results.',
        question4:
            'Your organization ensures that screenings and assessments are conducted by qualified behavioral health professionals who have training and expertise in trauma-informed care.',
        question5:
            'All behavioral health clients are screened for trauma exposure and symptoms during intake or early in treatment.',
        question6: 'Staff/providers use standardized trauma screening and assessment tools and processes.',
        question7:
            'Staff use non-technical language to discuss and interpret screening and assessment results with clients.',
        question8:
            'Following positive screenings for trauma. providers make timely referrals to (or provide) comprehensive trauma assessment and treatment.',
        question9:
            'Clinicians periodically repeat standardized trauma screening/assessment measures to refine and revise individualized treatment plans.',
        question10:
            'Organizations regularly evaluate client care trends to inform organizational practices and programming.',
        pageNumber: '4',
    },
    page5: {
        show: true,
        title: 'Workforce Development',
        pageNumber: '5',
    },
    page6: {
        show: true,
        question1:
            'During staff interview processes, interviewers assess candidates’ trauma-related knowledge, understanding, and attitudes.',
        question2:
            'Your organization’s new employee onboarding training includes foundational training on what trauma is, how it impacts people’s well-being, and understanding a trauma-informed approach.',
        question3:
            'All staff regularly use trauma-informed relational skills, such as fostering safety and trust, communicating with transparency, working collaboratively, and providing peer support.',
        question4:
            'Your organization engages experts to provide consultation and training related to cultural responsiveness, equity, and inclusion.',
        question5:
            'After a challenging or critical incident that impacts safety (i.e., physical, psychological, cultural, moral and/or social safety), staff are provided with timely support or resource.',
        question6:
            'Your organization supports staff retention by promoting resilience, self-awareness, a sense of hope, healthy coping, strong relationships, and ways to find meaning in their work.',
        question7:
            'Your organization develops and promotes supervisors and leaders who prioritize and model trauma sensitivity and encourage others to do the same.',
        question8:
            'Your organization provides equitable opportunities for career progression and develops leaders from a diverse pool of candidates.',
        question9:
            'Your organization regularly seeks and applies staff feedback on training needs, supervision, workplace culture and organizational climate.',
        pageNumber: '6',
    },
    page7: {
        show: true,
        title: 'Strengthening Protective Factors',
        pageNumber: '7',
    },
    page8: {
        show: true,
        question1:
            'Providers offer information to clients about resources to meet their basic needs (e.g., food, health care, shelter, safety, transportation) to reduce stress.',
        question2:
            'All staff/providers use culturally relevant practices to strengthen safety and trust in professional, family, and community relationships, as appropriate.',
        question3:
            'When appropriate, providers work to engage client family members (e.g., caregivers, siblings, elders, kin, chosen family, etc.) to strengthen relationships and mutual support.',
        question4:
            'Strengths-based assessments are conducted to identify protective factors, including cultural values, faith/spiritual beliefs, social connectedness, cultural/traditional healing practices of clients, etc.',
        question5:
            'Child service providers support children/youth with developing social and emotional skills (e.g., self-management, social awareness, relationship skills, responsible decision-making, etc.).',
        question6:
            'Your organization offers services for caregivers and parents of child clients with building skills for managing stress and fostering healing for the family unit.',
        question7:
            'Providers help clients build skills to establish and maintain a social support network they can rely on during their healing journey.',
        pageNumber: '8',
    },
    page9: {
        show: true,
        title: 'Addressing the Impact of Trauma on Families',
        pageNumber: '9',
    },
    page10: {
        show: true,
        question1:
            'All staff are trained on how public service systems (e.g., behavioral health, child welfare, criminal justice) can inadvertently traumatize or re-traumatize clients and ways to mitigate this.',
        question2:
            'Your organization’s physical environment (waiting area, offices, etc.) is designed to promote well-being and reduce the risk of re-traumatizing clients and families.',
        question3:
            'Providers tailor care to client and family cultural history, recognizing that culture can impact trauma disclosure and treatment participation.',
        question4:
            'Your organization offers training, supervision and/or consultation to staff on how to implement trauma-informed care.',
        question5:
            'Providers routinely offer psychoeducation to families about the impact that trauma exposure may have on parenting/caregiving.',
        question6:
            'Child and family service providers routinely invite family members to share personal trauma histories and healing practices that support their well-being.',
        question7:
            'Providers offer appropriate referrals to (or provide) trauma-informed services to client family members/caregivers when indicated based on their reported trauma exposure/symptoms.',
        pageNumber: '10',
    },
    page11: {
        show: true,
        title: 'Continuity of Care & Cross-Systems Collaboration',
        pageNumber: '11',
    },
    page12: {
        show: true,
        question1:
            'Child and family service providers use care-planning processes that include input from multiple sources, such as the child/youth, parents/caregivers, extended family or kin, schools, etc.',
        question2:
            'Providers who serve on cross-agency/cross-system care teams help other agencies understand client trauma-related exposures, behavioral impacts, and treatment needs.',
        question3:
            'Your organization allocates resources (e.g., protected time, communication tools) to support direct care providers with collaborating with other providers across systems to support client care.',
        question4:
            'Providers conduct regular meetings (e.g., multidisciplinary team meetings, individualized education planning) that include service providers from multiple systems to coordinate client care.',
        question5:
            'Providers have regular contact with other service providers to enhance care coordination (e.g., Memoranda of Understanding, open channels of communication between providers, built-in scheduling).',
        question6:
            'Providers use tools and practices that promote the sharing of trauma-related information between systems to support coordinated client care.',
        question7:
            'Your organization’s leadership and/or designated staff meet regularly with providers from other systems (e.g., social services, child welfare, juvenile services) to maintain effective and consistent trauma-informed care across systems.',
        pageNumber: '12',
    },
    page13: {
        show: true,
        title: 'Addressing Compassion Fatigue',
        pageNumber: '13',
    },
    page14: {
        show: true,
        question1:
            'Your organization ensures that staff at all levels understand how staff burnout and secondary traumatic stress can impact their ability to do their job effectively.',
        question2:
            'Staff receive training, supervision and/or consultation on stress management and well-being promotion strategies.',
        question3:
            'Your organization ensures that staff at all levels receive training on managing their reactions and responses to working with clients/people with lived experience of trauma/adversity.',
        question4:
            'Your organization offers timely resources and/or referrals to support for staff who are experiencing burnout or secondary traumatic stress.',
        question5:
            'Your organization seeks input from staff to establish policies and practices that promote well-being and prevent staff burnout and secondary traumatic stress.',
        question6:
            'Your organization facilitates strong peer support among staff to mitigate the impact of burnout and secondary traumatic stress.',
        question7:
            'Your organization has procedures and practices that enhance physical safety and actively manage risk to protect all staff.',
        pageNumber: '14',
    },
    page15: {
        show: true,
        title: 'Partnering with People with Lived Experience',
        pageNumber: '15',
    },
    page16: {
        show: true,
        question1:
            'Your organization collaborates with people with lived experience as advisors that inform the evaluation of agency practices and programs (e.g., development, interpretation of data, action planning).',
        question2:
            'Advisors/representatives with lived experience to your organization represent the diverse cultures and identities of the community served.',
        question3:
            'Your organization offers trainings for the workforce that are co-developed and/or co-delivered by providers and people with lived experience, when appropriate.',
        question4:
            'Your organization offers advisors/representatives with lived experience opportunities to partner with the agency in ways that are accessible to all by way of offering convenient locations and times, transportation, childcare, etc.',
        question5:
            'Advisors/representatives with lived experience are adequately prepared for meetings, events, leadership roles, and their overall involvement with the agency.',
        question6:
            'Your organization ensures that advisors/representatives with lived experience feel valued and respected in their consultative relationship with the agency.',
        pageNumber: '16',
    },
    page17: {
        show: true,
        title: 'Addressing the Intersections of Culture, Race & Trauma',
        pageNumber: '17',
    },
    page18: {
        show: true,
        question1:
            'Your organization’s physical environment features artwork, symbols, language, and imagery that reflect the diverse identities and cultures of the community it serves.',
        question2:
            'Staff/providers demonstrate respect for all clients by recognizing diverse family structures, a range of parenting practices, and a variety of options for accessing and utilizing healing supports.',
        question3:
            'All staff, clients, and visitors are invited to use their preferred identifiers (e.g., chosen names, pronouns) to describe themselves and/or their families.',
        question4:
            'All staff, including leadership, receive professional development support (e.g. performance reviews, training) to help them foster equitable care and culture in the organization.',
        question5:
            'Staffing at all levels of your organization, including management, reflects the diversity of the community being served.',
        question6:
            'Your organization offers training, supervision and/or consultation for all staff and leaders to increase everyone’s use of language and behaviors that foster inclusivity and belonging for all, including people of color and those from other marginalized communities.',
        question7:
            'Providers are given time and other resources (e.g., training, consultation, access to interpreter/translation services) they need to provide culturally and linguistically appropriate services.',
        question8:
            'Providers connect clients to community services and support that are responsive to their linguistic and cultural preferences.',
        question9:
            'Your organization is committed to including advisors/partners (e.g., advisory board members, trainers, consultants) of color and from other historically marginalized communities (e.g., LGBTQ+, immigrants, etc.) in all aspects of its work.',
        question10:
            'Your organization has a grievance/complaint process that includes a way to report concerns about discrimination and bias, including racism and other forms of oppression.',
        pageNumber: '18',
    },
    page19: {
        show: true,
        title: 'Governance, Leadership, and Financing',
        pageNumber: '19',
    },
    page20: {
        show: true,
        question1:
            'Your organization’s guiding principles and strategic plans (vision, mission, and goals) reflect a commitment to trauma-informed, resilience-oriented, and equitable care and culture.',
        question2:
            'Your organization implements a trauma-informed agency self-assessment to inform internal improvements to trauma-informed practices.',
        question3:
            'Your organization’s leadership openly communicates to staff, clients and other agencies about its efforts to advance trauma-informed, resilience oriented, and equitable policies and practices.',
        question4:
            'Your organization’s leadership gathers and considers staff input during the implementation of organizational policies and practices.',
        question5:
            'Your organization has dedicated staff to work on improving organizational policies and practices to be more trauma-informed (this responsibility is integrated into staff job descriptions).',
        question6:
            'Your organization provides resources (e.g., staffing, funds, professional development) to support its trauma-informed strategic vision, mission, and goals.',
        question7:
            'Your organization allocates funds to support trauma-informed care training and professional development for all leadership and staff.',
        question8:
            'Your organization prioritizes funding opportunities, such as licensing, grants, and contracts, related to trauma-informed care or trauma-informed organizational change.',
        pageNumber: '20',
    },
    page21: {
        show: true,
        title: 'Quality Improvement Strategic Planning Guide',
        pageNumber: '21',
    },
    page22: {
        show: true,
        title: 'Strategic Planning Guide',
        paragraph1:
            '(For example, for the Screening domain, one goal might be that the organization will better collect and use data to identify clients who need further trauma assessment.)',
        paragraph2:
            '(For example, for the Screening domain, your goal is to better collect and use data to identify clients who need further trauma assessment. One way of measuring success might be that, by the next year, the organization is reviewing screening data quarterly to track the number of clients in need of further assessment and the number of clients connected to further assessment.)',
        Q1: 'What opportunities exist related to this goal?',
        Q1_1: 'What have been our past successes?',
        Q1_2: 'What current work is taking place related to this goal?',
        Q1_3: 'What are our available resources (leadership, infrastructure, staffing, partnerships)?',
        Q2: 'What barriers exist related to this goal?',
        Q2_1: 'What would prevent us from moving forward with this goal?',
        Q2_2: 'What would we need to overcome this/these barrier(s)?',
        Q2_3: 'What are our available resources (leadership, infrastructure, staffing, partnerships)?',
        Q3: 'How does this goal foster trauma-informed, resilience-oriented, and equitable care and culture within the organization?',
        pageNumber: '22',
    },
    page23: {
        show: true,
        pageNumber: '23',
    },
    page24: {
        show: true,
        title: 'Strategic Planning Guide',
        pageNumber: '24',
    },
    page25: {
        show: true,
        pageNumber: '25',
    },
};

const Component = () => {
    let builderBlocks = [];
    for (const valueKey of Object.keys(initialValues)) {
        if (Object.hasOwnProperty.call(initialValues, valueKey)) {
            const dict = initialValues[valueKey];
            let fields = [];

            for (const key of Object.keys(dict)) {
                if (key === 'show') {
                    fields.push(
                        <Field
                            name={valueKey + '.' + key}
                            component={CccisdToggle}
                            label={`Show ${startCase(valueKey)}?`}
                        />
                    );
                    continue;
                }

                fields.push(
                    <Field
                        name={valueKey + '.' + key}
                        component={CccisdInput}
                        label={key.includes('_') ? key.replace('_', '.') : startCase(key)}
                    />
                );
            }
            builderBlocks.push(
                <BuilderBlock title={startCase(valueKey)} key={valueKey}>
                    {fields}
                </BuilderBlock>
            );
        }
    }

    return <>{builderBlocks}</>;
};

export default reportTemplateBuilder({})(Component);
